import { useCallback, useEffect, useState } from "react";
import { Plus, Search } from "lucide-react";
import api from "../../lib/api";
import { KIND_CONFIG } from "./walletConfig";
import { WalletItemForm } from "./WalletItemForm";
import { WalletItemCard } from "./WalletItemCard";

const VIEWS = [["active", "Active"], ["expiring", "Expiring Soon"], ["used", "Used"], ["expired", "Expired"], ["archived", "Archived"], ["all", "All"]];

/* Generic per-kind list: filter chips + search + add form + item cards. */
export function WalletList({ kind, trips, onChanged, renderExtra }) {
  const cfg = KIND_CONFIG[kind];
  const [items, setItems] = useState([]);
  const [view, setView] = useState("active");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null | {} | item

  const load = useCallback(() => {
    api.get(`/wallet/items/${kind}`, { params: { view, q } })
      .then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [kind, view, q]);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); onChanged?.(); };

  return (
    <div data-testid={`wallet-list-${kind}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex flex-wrap gap-1">
          {VIEWS.map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`wallet-view-${kind}-${v}`}
              className={`px-2.5 py-1 text-xs font-600 border ${view === v ? "bg-primary text-white border-primary" : "border-zinc-300 bg-white hover:border-primary"}`}>{l}</button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid={`wallet-search-${kind}`}
            className="border border-zinc-300 pl-7 pr-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-primary w-36" />
        </div>
        <button onClick={() => setEditing(editing ? null : {})} data-testid={`wallet-add-${kind}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-sm font-600 hover:bg-blue-700">
          <Plus className="w-4 h-4" /> Add {cfg.label.toLowerCase()}
        </button>
      </div>
      <p className="text-xs text-muted-foreground mb-3">{cfg.desc}</p>

      {editing !== null && (
        <WalletItemForm kind={kind} initial={editing.id ? editing : undefined}
          onSaved={() => { setEditing(null); changed(); }} onCancel={() => setEditing(null)} />
      )}

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid={`wallet-empty-${kind}`}>
          Nothing here yet — tap "Add {cfg.label.toLowerCase()}" to start tracking.
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => (
            <span key={it.id} className="contents">
              <WalletItemCard kind={kind} it={it} trips={trips}
                onEdit={() => { const clean = { ...it }; (["number", "rewards_number", "admin_number"]).forEach((k) => { if (clean[`has_${k}`] !== undefined) clean[k] = ""; }); setEditing(clean); }}
                onChange={changed} />
              {renderExtra?.(it, changed)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
